import { ApolloError } from '@apollo/client';

import { useErpVacanciesQuery } from 'core/graphql';

import { useStableRefetch } from '../Groups/useStableRefetch';
import { IVacancy } from './careerModel';

// Stable empty fallback — see the note in `useCareerMembers`.
const EMPTY_VACANCIES: IVacancy[] = [];

interface IVacanciesFilters {
  status?: string;
  directionId?: string;
  search?: string;
}

interface IVacanciesHook {
  vacancies: IVacancy[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Spec 0018 increment B — the Вакансии tab (`careerVacanciesErp`). Returns
 * every status (DRAFT included), unlike the LMS `careerVacancies`. Filter
 * changes re-run the query through Apollo's variable tracking; an empty filter
 * goes as `undefined` so the backend applies no narrowing.
 */
export const useVacancies = (
  filters: IVacanciesFilters = {},
): IVacanciesHook => {
  const { data, loading, error, refetch } = useErpVacanciesQuery({
    variables: {
      status: filters.status || undefined,
      directionId: filters.directionId || undefined,
      search: filters.search || undefined,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Pinned identity — this flows into the mutation hook whose callbacks land in
  // the react-table `columns` memo; an unstable one loops the render.
  const stableRefetch = useStableRefetch(refetch);

  return {
    vacancies: data?.careerVacanciesErp ?? EMPTY_VACANCIES,
    loading,
    error,
    refetch: stableRefetch,
  };
};
